import { getProductBreadcrumb, SeoHeader, SeoProductBreadcrumb, SeoProductGroup } from './Seo'

/* Next */
import Link from 'next/link'
import { useRouter } from 'next/router'

/* React */
import { memo, useCallback, useState, useEffect, useMemo } from 'react'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import useStyles from './styles'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'

/* Types */
import { tProduct } from 'src/shared/frontendTypes/product'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'
import { useLiveProductCommerce } from 'src/hooks/useLiveProductCommerce'
import { useManufacturerGuarantees } from 'src/hooks/useManufacturerGuarantees'
import { ManufacturerGuaranteeLabels } from 'src/components/ManufacturerGuarantees'

/* Functions */
import getCombination from './functions/getCombination'
import {
  buildDefaultSelectedAttributes,
  buildDisabledAttributeOptions,
  buildSelectedAttributesFromOptions,
  normalizeSelectedAttributes,
  selectedAttributesAreEqual,
} from './functions/attributeAvailability'
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import { hrefForBrandPath } from 'src/functions/navigationBreadcrumbs'
import { pageBuilderButtonSx } from 'src/theme/pageBuilderButtons'

/* Components */
import { selectedAttributes } from './Components/AddToCart/Components/AttributesSelector'
import ImagesCarousel from './Components/ImagesCarousel/ImagesCarousel'
import AddToCart from './Components/AddToCart/AddToCart'
import DescriptionAccordion from './Components/DescriptionAccordion/DescriptionAccordion'
import ProductRecommendations from './ProductRecommendations'
import { useProductInquiryConfig } from 'src/context/ProductInquiryContext'
import { BreadcrumbTrail } from 'src/components/Breadcrumbs/Breadcrumbs'
import BrandLogo from './Components/BrandLogo'

/* Shop-Config */
import shopConfig from 'template/config'

const MemoAddToCart = memo(AddToCart)
const emptyPurchased: NonNullable<tProduct['alsoPurchased']> = []
const emptyGuarantees: NonNullable<tProduct['manufacturerGuarantees']> = []

const ProductSeo = memo(({ product }: { product: tProduct }) => <>
  <SeoHeader
    id={product.id}
    metaTitle={product.metaTitle}
    productName={product.name}
    path={product.path}
    descriptionShort={product.descriptionShort}
    descriptionDetails={product.descriptionDetails}
    description={product.description}
    keywords={product.keywords}
    images={product.images}
  />
  <SeoProductBreadcrumb product={product} />
  <SeoProductGroup product={product} />
  <BreadcrumbTrail items={getProductBreadcrumb(product)} />
</>)
ProductSeo.displayName = 'ProductSeo'

const magazineReturnStorageKey = 'dmok-magazine-return'

type MagazineReturnState = {
  hash: string
  page?: number
  title?: string
}

const buildInitialSelectedAttributes = (product: tProduct, variantKey?: string) => {
  const variant = variantKey
    ? product.combinations.find(combination => combination.key === variantKey)
    : undefined

  return normalizeSelectedAttributes(
    product,
    variant
      ? buildSelectedAttributesFromOptions(product, variant.options)
      : buildDefaultSelectedAttributes(product),
  )
}

const Product: React.FC<{
  product: tProduct
  recommendationsDeferred?: boolean
  initialVariantKey?: string
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}> = ({
  product,
  recommendationsDeferred = false,
  initialVariantKey,
  landingPageImageAiGenerated,
  product: {
    brand,
    brandTitel,
    brandLogo,
    brandPath,
    name,
    onlyStock,
    images: productImages,
    id,
    path,
  },
}) => {
  const classes = useStyles()
  const router = useRouter()
  const productInquiry = useProductInquiryConfig()
  const [magazineReturn, setMagazineReturn] = useState<MagazineReturnState | null>(null)

  // Attributes ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  const [selectedAttributes, setOption] = useState<selectedAttributes>(
    () => buildInitialSelectedAttributes(product, initialVariantKey)
  )

  const replaceVariantPath = useCallback((nextSelection: selectedAttributes) => {
    if (typeof window === 'undefined') return

    const allAttributesSelected = (product.attributes || []).every(attribute => (
      typeof nextSelection[attribute.id] === 'number'
    ))
    const nextCombination = allAttributesSelected
      ? getCombination(product, nextSelection)
      : null
    const nextUrl = nextCombination
      ? productVariantUrl(id, path, nextCombination.key)
      : productUrl(id, path)

    window.history.replaceState(window.history.state, '', `${nextUrl}${window.location.search}`)
  }, [product, id, path])

  const handleChangeAttribute = useCallback((attribut: number, option: number | '') => {
    const nextSelection = normalizeSelectedAttributes(
      product,
      { ...selectedAttributes, [attribut]: option },
      attribut,
    )

    setOption(nextSelection)
    replaceVariantPath(nextSelection)
  }, [product, selectedAttributes, replaceVariantPath])

  const handleChangeOptions = (options: number[]) => {
    const nextSelection = buildSelectedAttributesFromOptions(product, options)
    setOption(nextSelection)
    replaceVariantPath(nextSelection)
  }

  useEffect(() => {
    const initialSelection = buildInitialSelectedAttributes(product, initialVariantKey)
    setOption(current => selectedAttributesAreEqual(current, initialSelection)
      ? current
      : initialSelection)
  }, [initialVariantKey, product])

  useEffect(() => {
    const hashCheck = () => {
      const hash = window.location.hash
      if (hash.match(/^#[0-9-]+$/)) {
        const options = hash
          .replace('#', '')
          .split('-')
          .map((o) => Number(o))
        if (options.length > 0) handleChangeOptions(options)
      }
    }
    hashCheck()

    router.events.on('routeChangeComplete', hashCheck)
    return () => router.events.off('routeChangeComplete', hashCheck)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.events, router.query])

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const raw = window.sessionStorage.getItem(magazineReturnStorageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as MagazineReturnState
      if (parsed?.hash?.startsWith('#magazin')) setMagazineReturn(parsed)
    } catch {
      window.sessionStorage.removeItem(magazineReturnStorageKey)
    }
  }, [])

  const handleReturnToMagazine = () => {
    if (typeof window === 'undefined' || !magazineReturn) return

    const targetHash = magazineReturn.hash
    window.sessionStorage.removeItem(magazineReturnStorageKey)
    setMagazineReturn(null)
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    window.setTimeout(() => {
      window.location.hash = targetHash
    }, 0)
  }

  // Combination +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  const combination = useMemo(() => getCombination(product, selectedAttributes), [product, selectedAttributes])
  const liveGuarantees = useManufacturerGuarantees(combination ? [{ id, key: combination.key }] : [])
  const guaranteeItem = useMemo(() => {
    if (!combination) return undefined
    const liveItems = liveGuarantees.items
    const guarantees = liveItems !== undefined
      ? liveItems[0]?.guarantees
      : product.manufacturerGuarantees?.map(guarantee => {
        const labelEntry = product.manufacturerGuaranteeLabels?.[combination.key]?.find(
          item => item.programId === guarantee.programId,
        )
        return { ...guarantee, label: labelEntry?.label, revision: labelEntry?.revision || guarantee.revision }
      })
    return { id, key: combination.key, name, guarantees: guarantees?.length ? guarantees : emptyGuarantees }
  }, [id, name, combination, liveGuarantees.items, product.manufacturerGuarantees, product.manufacturerGuaranteeLabels])
  const disabledAttributeOptions = useMemo(
    () => buildDisabledAttributeOptions(product, selectedAttributes),
    [product, selectedAttributes],
  )
  const commerceSource = useMemo(() => combination
      ? {
        id,
        key: combination.key,
        ...(combination.hash ? { hash: combination.hash } : {}),
        price: typeof combination.price === 'number'
          ? combination.price
          : combination.price.regularPrice,
        deliverytime: combination.deliverytime,
        ...(typeof combination.stockDeliverytime === 'number' ? { stockDeliverytime: combination.stockDeliverytime } : {}),
        ...(typeof combination.stock === 'number' ? { stock: combination.stock } : {}),
        ...(combination.discount ? { discount: combination.discount } : {}),
        ...(product.couponPromotion ? { couponPromotion: product.couponPromotion } : {}),
        ...(combination.priceLock ? { priceLock: combination.priceLock } : {}),
        ...(product.purchaseMode ? { purchaseMode: product.purchaseMode } : {}),
        ...(product.inquireOnly ? { inquireOnly: product.inquireOnly } : {}),
      }
      : null, [combination, id, product.couponPromotion, product.purchaseMode, product.inquireOnly])
  const liveCombinationCommerce = useLiveProductCommerce(commerceSource)
  const renderedCombination = useMemo(() => combination
    ? {
      ...combination,
      price: liveCombinationCommerce?.discount
        ? {
          regularPrice: liveCombinationCommerce.price,
          discountPrice: liveCombinationCommerce.discount.price,
          discountPriceAfterStock: liveCombinationCommerce.discount.priceAfterStock,
          stockDiscountLimit: liveCombinationCommerce.discount.stockDiscountLimit,
          limitStockDiscountToStock: liveCombinationCommerce.discount.limitStockDiscountToStock,
          quantityDiscount: liveCombinationCommerce.discount.quantityDiscount,
          title: liveCombinationCommerce.discount.metaTitle,
          type: liveCombinationCommerce.discount.type,
          countdownVariant: liveCombinationCommerce.discount.countdownVariant,
          dateEnd: liveCombinationCommerce.discount.dateEnd,
          showPercent: liveCombinationCommerce.discount.showPercent,
        }
        : liveCombinationCommerce?.price ?? combination.price,
      deliverytime: liveCombinationCommerce?.deliverytime ?? combination.deliverytime,
      stockDeliverytime: liveCombinationCommerce?.stockDeliverytime ?? combination.stockDeliverytime,
      stock: typeof liveCombinationCommerce?.stock === 'number'
        ? liveCombinationCommerce.stock
        : combination.stock,
      discount: liveCombinationCommerce?.discount,
      priceLock: liveCombinationCommerce?.priceLock ?? combination.priceLock,
      hash: liveCombinationCommerce?.hash ?? combination.hash,
    }
    : null, [combination, liveCombinationCommerce])
  const livePurchaseMode = liveCombinationCommerce
    ? liveCombinationCommerce.purchaseMode || 'cart'
    : undefined
  const renderedProduct = useMemo(() => liveCombinationCommerce
    ? {
        ...product,
        purchaseMode: livePurchaseMode,
        inquireOnly: livePurchaseMode === 'inquiry' ? true as const : undefined,
        couponPromotion: liveCombinationCommerce.couponPromotion ?? undefined,
      }
    : product, [product, liveCombinationCommerce, livePurchaseMode])
  const brandHref = hrefForBrandPath(brandPath, brand)

  // Images ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  const images = renderedCombination?.images || productImages

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  return (
    <Container component="article" maxWidth="lg" className={classes.container}>
      <ProductSeo product={product} />

      {shopConfig.productLagerwareTopAlert && onlyStock && (
        <Alert severity="error" className={classes.errorAlert}>
          Auslaufartikel, nur solange der Vorrat reicht.
        </Alert>
      )}

      {renderedProduct.inquireOnly && (
        <Alert severity='info' className={classes.errorAlert}>
          {productInquiry.detailNotice}
        </Alert>
      )}

      <div className={classes.productTitle}>
        <div className={classes.brandHeader}>
          {brandHref
            ? <Link href={brandHref} passHref legacyBehavior>
                <a className={classes.brandHeaderLink}>
                  {brandLogo && (
                    <span className={classes.brandHeaderLogo}>
                      <BrandLogo logo={brandLogo} title={brandTitel} />
                    </span>
                  )}
                  <span>{brandTitel}</span>
                </a>
              </Link>
            : <span className={classes.brandHeaderLink}>
                {brandLogo && (
                  <span className={classes.brandHeaderLogo}>
                    <BrandLogo logo={brandLogo} title={brandTitel} />
                  </span>
                )}
                <span>{brandTitel}</span>
              </span>
          }
        </div>
        <Typo component="h1" variant="h4" color="inherit" gutterBottom>
          {name}
        </Typo>
      </div>

      <div className={classes.grid}>
        <div className={classes.imagesCarousel}>
          <ImagesCarousel {...{ product, images }} />
        </div>

        <div className={classes.addToCart}>
          <MemoAddToCart
            {...{
              product: renderedProduct,
              combination: renderedCombination,
              selectedAttributes,
              disabledAttributeOptions,
              handleChangeAttribute,
            }}
          />
          {(product.manufacturerGuaranteesUnavailable && !liveGuarantees.ready
            || liveGuarantees.error && Boolean(product.manufacturerGuarantees?.length)) && <Alert severity='warning' sx={{ mt: 2 }}>
            Die Informationen zur Herstellergarantie können gerade nicht vollständig angezeigt werden. Bitte laden Sie die Seite erneut, bevor Sie die Bestellung abschließen.
          </Alert>}
          <ManufacturerGuaranteeLabels item={guaranteeItem} />
        </div>
      </div>

      <hr />

      <DescriptionAccordion
        {...{ product, landingPageImageAiGenerated }}
        alsoPurchased={recommendationsDeferred ? emptyPurchased : product.alsoPurchased}
        manufacturerGuarantees={guaranteeItem?.guarantees}
      />

      <ProductRecommendations
        product={product}
        deferred={recommendationsDeferred}
        combination={renderedCombination}
        selectedAttributes={selectedAttributes}
      />

      {magazineReturn &&
        <Button
          className={classes.magazineReturnButton}
          variant='contained'
          startIcon={<AutoStoriesIcon />}
          sx={pageBuilderButtonSx}
          onClick={handleReturnToMagazine}
        >
          Zurück ins Magazin
        </Button>
      }

    </Container>
  )
}

export default Product
